import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormGroupDirective, NgForm, Validators } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { BaseComponent } from 'src/app/core/components/base.component';
import { LanguageModel } from 'src/app/core/services/api-services';
import { LanguageService as ApiLanguageService } from 'src/app/core/services/api-services';
import { LanguageService } from 'src/app/core/services/language.service';
import { MessageService } from 'src/app/core/services/message.service';
import CustomValidators from '../../core/validators/custom-validators.validator';

@Component({
    selector: 'app-people-detail',
    templateUrl: './people-detail.component.html',
    styleUrls: ['./people-detail.component.scss'],
    standalone: false
})

export class PeopleDetailComponent extends BaseComponent implements OnInit {
  @Input() disabled: boolean = false;
  @Input() singleBooking: boolean = false;
  @Input() diplomatBooking: boolean = false;
  @Input() showMailToggle: boolean = false;
  @Input() showCorrespondenceLanguage: boolean = true;
  
  @Input() showAddress$: Observable<boolean> = of(true);
  @Input() showSimple$: Observable<boolean> = of(false);
  @Input() showSimplest$: Observable<boolean> = of(false);
  @Input() isParliamentPerson$: Observable<boolean> = of(false);
  @Input() showParliamentPersonFields: boolean = false;
  
  showMailToggleControl = new FormControl<boolean | null>(false);
  @Output() onShowMailToggle = new EventEmitter<boolean | null>();

  @ViewChild('formDirective') formDirective!: FormGroupDirective;

  languages: LanguageModel[] = [];
  browserLanguage!: LanguageModel;
  showAddress: boolean = true;
  showSimple: boolean = false;
  showSimplest: boolean = false;
  isParliamentPerson: boolean = false;

  constructor(@Inject(LOCALE_ID) private readonly locale: string,
    private readonly languagesService: ApiLanguageService,
    readonly languageService: LanguageService,
    private readonly messageService: MessageService) {
      super();
  }

  formGroup = new FormGroup({
    id: new FormControl<string | null>(null, { validators: [] }),
    name: new FormControl<string | null>(null, { validators: [Validators.required, CustomValidators.hasForbiddenCharacters()] }),
    prename: new FormControl<string | null>(null, { validators: [Validators.required, CustomValidators.hasForbiddenCharacters()] }),
    organization: new FormControl<string | null>(null, { validators: [CustomValidators.hasForbiddenCharacters()] }),
    street: new FormControl<string | null>(null, { validators: [Validators.required, CustomValidators.hasForbiddenCharacters()] }),
    number: new FormControl<string | null>(null, { validators: [Validators.required, CustomValidators.hasForbiddenCharacters()] }),
    zip: new FormControl<string | null>(null, { validators: [Validators.required, CustomValidators.hasForbiddenCharacters()] }),
    location: new FormControl<string | null>(null, { validators: [Validators.required, CustomValidators.hasForbiddenLocationCharacters()] }),
    mail: new FormControl<string | null>(null, { validators: [CustomValidators.email, Validators.required, CustomValidators.hasForbiddenCharacters()] }),
    mobile: new FormControl<string | null>(null, { validators: [Validators.required, CustomValidators.internationalPhone] }),
    identificationNumber: new FormControl<string | null>(null, { validators: [CustomValidators.hasForbiddenCharacters()] }),
    language: new FormControl<string | null>(null, { validators: [Validators.required] })
  });

  ngOnInit(): void {
    this.languagesService.getAll(false).subscribe(
      languages => {
        this.languages = languages !== null ? languages : [];
        this.browserLanguage = this.languages.find(l => l.languageCode.toUpperCase() === this.locale.toString().toUpperCase())!;

        if (!this.formGroup.controls.language.value) {
          this.formGroup.controls['language'].setValue(this.browserLanguage?.languageCode, { onlySelf: true });
        }
      });

    if (this.disabled == true) {
      this.formGroup.disable();
    }

    this.showAddress$
    .unsubscribeOnDestroy(this)
    .subscribe(val => {

      this.showAddress = val;

      if (this.showAddress == false) {
        this.streetControl.setValidators(null);
        this.streetControl.updateValueAndValidity();
        this.numberControl.setValidators(null);
        this.numberControl.updateValueAndValidity();
        this.zipControl.setValidators(null);
        this.zipControl.updateValueAndValidity();
        this.locationControl.setValidators(null);
        this.locationControl.updateValueAndValidity();
      } else {
        this.streetControl.setValidators([Validators.required, CustomValidators.hasForbiddenCharacters()]);
        this.streetControl.updateValueAndValidity();
        this.numberControl.setValidators([Validators.required, CustomValidators.hasForbiddenCharacters()]);
        this.numberControl.updateValueAndValidity();
        this.zipControl.setValidators([Validators.required, CustomValidators.hasForbiddenCharacters()]);
        this.zipControl.updateValueAndValidity();
        this.locationControl.setValidators([Validators.required, CustomValidators.hasForbiddenLocationCharacters()]);
        this.locationControl.updateValueAndValidity();
      }
    });

    this.showSimple$
    .unsubscribeOnDestroy(this)
    .subscribe(val => {

      this.showSimple = val;
      
      if (this.showSimple == true) {
        this.mobileControl.setValidators(null);
        this.mobileControl.updateValueAndValidity();
        this.mailControl.setValidators(null);
        this.mailControl.updateValueAndValidity();
      } else {
        this.mobileControl.setValidators([Validators.required, CustomValidators.internationalPhone, CustomValidators.hasForbiddenCharacters()]);
        this.mobileControl.updateValueAndValidity();
        this.mailControl.setValidators([Validators.required, CustomValidators.email, CustomValidators.hasForbiddenCharacters()]);
        this.mailControl.updateValueAndValidity();
      }
    });

    this.showSimplest$
    .unsubscribeOnDestroy(this)
    .subscribe(val => {

      this.showSimplest = val;

      if (this.showSimplest == true) {
        this.languageControl.setValidators(null);
        this.languageControl.updateValueAndValidity();
      } else {
        this.languageControl.setValidators([Validators.required, CustomValidators.hasForbiddenCharacters()]);
        this.languageControl.updateValueAndValidity();
      }
    });

    
    this.isParliamentPerson$
    .unsubscribeOnDestroy(this)
    .subscribe(val => {

      this.isParliamentPerson = val;

      if (this.isParliamentPerson == true) {
        this.streetControl.setValidators(null);
        this.streetControl.updateValueAndValidity();
        this.numberControl.setValidators(null);
        this.numberControl.updateValueAndValidity();
        this.zipControl.setValidators(null);
        this.zipControl.updateValueAndValidity();
        this.locationControl.setValidators(null);
        this.locationControl.updateValueAndValidity();
        this.mobileControl.setValidators(null);
        this.mobileControl.updateValueAndValidity();
        this.identificationNumberControl.setValidators(null);
        this.identificationNumberControl.updateValueAndValidity();
      } else {
        this.streetControl.setValidators([Validators.required, CustomValidators.hasForbiddenCharacters()]);
        this.streetControl.updateValueAndValidity();
        this.numberControl.setValidators([Validators.required, CustomValidators.hasForbiddenCharacters()]);
        this.numberControl.updateValueAndValidity();
        this.zipControl.setValidators([Validators.required, CustomValidators.hasForbiddenCharacters()]);
        this.zipControl.updateValueAndValidity();
        this.locationControl.setValidators([Validators.required, CustomValidators.hasForbiddenLocationCharacters()]);
        this.locationControl.updateValueAndValidity();
        this.mobileControl.setValidators([Validators.required, CustomValidators.internationalPhone]);
        this.mobileControl.updateValueAndValidity();
        this.identificationNumberControl.setValidators([CustomValidators.hasForbiddenCharacters()]);
        this.identificationNumberControl.updateValueAndValidity();
      }
    });

    this.formGroup.markAsPristine();    
    this.formGroup.markAsUntouched();
  }

  public findInvalidControls() {
    const invalid: string[] = [];
    const controls = this.formGroup.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  mailToggleChange(): void {
    this.showMailToggleControl.setValue(!this.showMailToggleControl.value)
  }

  updatePlace(place) {
    this.locationControl.setValue(place);
  }

  clearForm() {
    this.formDirective.resetForm();
  }

  resetForm(reset: boolean = true) {
    if (reset) {
      this.formGroup.reset();
    }

    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      control!.setErrors(null);
    });
  }

  getZipLabel(): string {
    return this.zipControl.hasValidator(Validators.required) ? 'Public.ContactData.ZipRequired' : 'Public.ContactData.Zip';
  }

  get streetControl(): FormControl {
    return this.formGroup.get('street') as FormControl;
  }

  get numberControl(): FormControl {
    return this.formGroup.get('number') as FormControl;
  }

  get zipControl(): FormControl<string> {
    return this.formGroup.get('zip') as FormControl<string>;
  }

  get locationControl(): FormControl {
    return this.formGroup.get('location') as FormControl;
  }

  get identificationNumberControl(): FormControl {
    return this.formGroup.get('identificationNumber') as FormControl;
  }

  get languageControl(): FormControl {
    return this.formGroup.get('language') as FormControl;
  }

  get mobileControl(): FormControl {
    return this.formGroup.get('mobile') as FormControl;
  }

  get mailControl(): FormControl {
    return this.formGroup.get('mail') as FormControl;
  }
}
